import { remove, omit, filter } from 'lodash';

import { Content, ContentFragment, ContentInput, GroupIdApproval, Network } from 'api';
import { IVideoInput, UserGroup } from 'types/app';
import {
  getVideo,
  hasBody,
  hasCommentary,
  hasDisclosures,
  hasGroup,
  hasPhoto,
  hasUrl,
  hasVideo,
} from 'features/content/helpers';

type ContentType = Content | ContentFragment;

export const bodyEndsWithUrl = (url: string, body: string) => {
  const urlIndex = body.lastIndexOf(url);

  if (urlIndex < 0) {
    return false;
  }

  return urlIndex + url.length === body.length;
};

export const stripTrailingUrl = (url: string, body: string) => body.replace(url, '');

export const getSafeBody = (body: string) => {
  return body === '' ? null : body;
};

export const formatBody = (content: ContentInput) => {
  const { content_object } = content;
  const { body, url } = content_object;

  if (bodyEndsWithUrl(url, body)) {
    return getSafeBody(stripTrailingUrl(url, body));
  }

  return getSafeBody(body);
};

export const formatCommentary = (content: ContentInput) => {
  if (!hasCommentary(content)) {
    return null;
  }

  return remove(content.commentary, commentary => !!commentary.commentary);
};

export const formatDisclosures = (content: ContentInput) => {
  if (!hasDisclosures(content)) {
    return [];
  }

  const disclosures = filter(content.disclosures, disclosure => !!disclosure.disclosure_id);
  return disclosures.map(item => omit(item, 'disclosure'));
};

export const isApprovedGroup = (content: ContentType, group: UserGroup) => {
  return content.group_id_approvals.some((approval: GroupIdApproval) => {
    return approval.group_id === group.id;
  });
};

export const isValidPost = (content: ContentInput) => {
  const containsContent = !!(
    hasBody(content) ||
    hasUrl(content) ||
    hasPhoto(content) ||
    hasVideo(content)
  );

  const video = getVideo(content);
  const hasNoPendingUploads = !video || (video && !video.file);

  return containsContent && hasNoPendingUploads && hasGroup(content);
};

export const uploadVideo = async (video: IVideoInput, onProgress: (value: number) => void) => {
  const amplifyStorage = await import('@aws-amplify/storage');
  const Storage = amplifyStorage.default;

  Storage.configure({
    AWSS3: {
      bucket: video.bucket,
      region: video.region,
    },
  });

  try {
    await Storage.put(video.key, video.file, {
      contentType: video.mimeType,
      customPrefix: {
        public: '',
      },
      progressCallback(progress: ProgressEvent) {
        const { loaded, total } = progress;
        onProgress((loaded / total) * 100);
      },
    });
  } catch (error) {
    throw error;
  }
};

export const getDisclosureNetwork = (network: Network) => {
  switch (network) {
    case 'facebook': {
      return Network.Facebook;
    }
    case 'twitter': {
      return Network.Twitter;
    }
    case 'linkedin': {
      return Network.Linkedin;
    }
    default: {
      return null;
    }
  }
};
